import { IdCard } from 'lucide-react';
import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import type { ModuleDefinition } from '../../app/modules';
import { usePeopleAvailable } from './api';

/**
 * Персонал (М12): працівники й профілі (посади, грейди, форма відносин, графік, особисті дані, посадова папка,
 * навчання й допуски), довідник посад, адаптація й рішення про допуск, реєстр порушень і «градієнт впливу»,
 * оцінка відповідності посаді, мотивація й бонуси, погодження відсутностей, завершення співпраці (HR),
 * налаштування (HR). Права вирішує сервер; інтерфейс показує дії відповідно до ролі й відповідей сервера.
 * Сторінки завантажуються ліниво — без циклічних імпортів із реєстром модулів.
 */
const page = (load: () => Promise<{ default: ComponentType }>): Pick<RouteObject, 'lazy'> => ({
  lazy: async () => ({ Component: (await load()).default }),
});

const peopleModule: ModuleDefinition = {
  key: 'people',
  label: 'Персонал',
  icon: IdCard,
  basePath: '/people',
  scope: 'users:read',
  // Модуль доступний, коли сервер відповідає на довідник посад (GET /api/v1/people/positions).
  useAccess: usePeopleAvailable,
  // Після «Автоматизації» (70): однаковий порядок давав би випадкову послідовність у меню.
  order: 75,
  routes: [
    {
      ...page(() => import('./PeopleLayout')),
      children: [
        { index: true, ...page(() => import('./pages/EmployeesPage')) },
        { path: 'positions', ...page(() => import('./pages/PositionsPage')) },
        { path: 'adaptations', ...page(() => import('./pages/AdaptationsPage')) },
        { path: 'adaptations/:adaptationId', ...page(() => import('./pages/AdaptationPage')) },
        { path: 'violations', ...page(() => import('./pages/ViolationsPage')) },
        { path: 'assessments', ...page(() => import('./pages/AssessmentsPage')) },
        { path: 'assessments/:assessmentId', ...page(() => import('./pages/AssessmentPage')) },
        { path: 'bonuses', ...page(() => import('./pages/BonusesPage')) },
        { path: 'absences', ...page(() => import('./pages/AbsenceRoutesPage')) },
        { path: 'offboarding', ...page(() => import('./pages/OffboardingsPage')) },
        { path: 'offboarding/new', ...page(() => import('./pages/OffboardingWizardPage')) },
        { path: 'offboarding/:offboardingId', ...page(() => import('./pages/OffboardingPage')) },
        { path: 'settings', ...page(() => import('./pages/SettingsPage')) },
        { path: ':userId', ...page(() => import('./pages/ProfilePage')) },
      ],
    },
  ],
};

export default peopleModule;
